import { useState } from 'react'
import { Tag, Trash2, X, Check, AlertCircle } from 'lucide-react'
import { api } from '../../api/client'

export interface EmailLabelProposal {
  id: string
  status: 'pending' | 'applied' | 'rejected' | 'failed'
  gmail_message_id: string
  gmail_subject?: string | null
  gmail_from?: string | null
  suggestions: string[]
  applied_action?: string | null
  error_message?: string | null
  created_at: string
  resolved_at?: string | null
}

interface Props {
  proposal: EmailLabelProposal
  onChange?: (updated: EmailLabelProposal) => void
}

// "Trash" is a sentinel in suggestions[] that routes to messages.trash on
// apply. Anything else is treated as a label name.
const TRASH_SENTINEL = 'Trash'

export function EmailLabelProposalCard({ proposal, onChange }: Props) {
  const [busy, setBusy] = useState<string | null>(null)  // which button is loading
  const [localError, setLocalError] = useState<string | null>(null)

  const applyLabel = async (label: string) => {
    setBusy(label)
    setLocalError(null)
    try {
      const body = label === TRASH_SENTINEL
        ? { action: 'trash' }
        : { action: 'label', label_name: label }
      const updated = await api.post<EmailLabelProposal>(
        `/chat/email-label-proposals/${proposal.id}/apply`,
        body,
      )
      onChange?.(updated)
    } catch (e: any) {
      setLocalError(e.message || 'Pedido falhou')
    } finally {
      setBusy(null)
    }
  }

  const reject = async () => {
    setBusy('__reject__')
    setLocalError(null)
    try {
      const updated = await api.post<EmailLabelProposal>(
        `/chat/email-label-proposals/${proposal.id}/reject`,
      )
      onChange?.(updated)
    } catch (e: any) {
      setLocalError(e.message || 'Pedido falhou')
    } finally {
      setBusy(null)
    }
  }

  const palette =
    proposal.status === 'applied'
      ? { border: 'var(--accent)', tint: 'rgba(45, 90, 61, 0.06)' }
      : proposal.status === 'failed'
        ? { border: '#dc2626', tint: '#fef2f2' }
        : proposal.status === 'rejected'
          ? { border: 'var(--border)', tint: 'rgba(0,0,0,0.02)' }
          : { border: 'var(--border)', tint: 'rgba(255,255,255,0.7)' }

  return (
    <div
      style={{
        marginTop: '12px',
        border: `1px solid ${palette.border}`,
        borderRadius: '12px',
        background: palette.tint,
        padding: '16px 18px',
        maxWidth: '520px',
        fontFamily: 'var(--sans)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        <Tag size={18} color="var(--accent)" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--serif)',
              fontSize: '15px',
              fontWeight: 600,
              color: 'var(--ink)',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={proposal.gmail_subject || proposal.gmail_message_id}
          >
            {proposal.gmail_subject || '(sem assunto)'}
          </div>
          {proposal.gmail_from && (
            <div
              style={{
                fontSize: '12px',
                color: 'var(--ink-light)',
                marginTop: '2px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {proposal.gmail_from}
            </div>
          )}
        </div>
        <StatusBadge status={proposal.status} appliedAction={proposal.applied_action ?? null} />
      </div>

      {/* Action buttons — only while pending */}
      {proposal.status === 'pending' && (
        <>
          <div
            style={{
              fontSize: '12px',
              color: 'var(--ink-muted)',
              marginBottom: '8px',
            }}
          >
            Arquivar com label ou mandar para o lixo:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {proposal.suggestions
              .filter((s) => s !== TRASH_SENTINEL)
              .map((label) => (
                <button
                  key={label}
                  onClick={() => applyLabel(label)}
                  disabled={busy !== null}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '999px',
                    border: '1px solid var(--accent)',
                    background: 'var(--accent)',
                    color: '#fff',
                    fontFamily: 'var(--sans)',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: busy ? 'wait' : 'pointer',
                    opacity: busy === label ? 0.6 : 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                  title={`Aplicar label "${label}"`}
                >
                  <Tag size={11} />
                  {busy === label ? 'A aplicar…' : label}
                </button>
              ))}
            {proposal.suggestions.includes(TRASH_SENTINEL) && (
              <button
                onClick={() => applyLabel(TRASH_SENTINEL)}
                disabled={busy !== null}
                style={{
                  padding: '6px 12px',
                  borderRadius: '999px',
                  border: '1px solid #dc2626',
                  background: 'transparent',
                  color: '#dc2626',
                  fontFamily: 'var(--sans)',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: busy ? 'wait' : 'pointer',
                  opacity: busy === TRASH_SENTINEL ? 0.6 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
                title="Mover para o lixo"
              >
                <Trash2 size={11} />
                {busy === TRASH_SENTINEL ? 'A mover…' : 'Lixo'}
              </button>
            )}
            <button
              onClick={reject}
              disabled={busy !== null}
              style={{
                padding: '6px 12px',
                borderRadius: '999px',
                border: '1px solid var(--border)',
                background: 'transparent',
                color: 'var(--ink-muted)',
                fontFamily: 'var(--sans)',
                fontSize: '12px',
                fontWeight: 500,
                cursor: busy ? 'wait' : 'pointer',
                opacity: busy === '__reject__' ? 0.6 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
              title="Ignorar sugestão"
            >
              <X size={11} />
              Ignorar
            </button>
          </div>
        </>
      )}

      {proposal.status === 'applied' && proposal.applied_action && (
        <div
          style={{
            marginTop: '4px',
            fontSize: '12px',
            color: 'var(--ink-light)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Check size={12} color="var(--accent)" />
          {proposal.applied_action === 'trash'
            ? 'Movido para o lixo'
            : `Label "${proposal.applied_action}" aplicado`}
        </div>
      )}

      {proposal.status === 'failed' && (
        <div
          style={{
            marginTop: '10px',
            padding: '8px 12px',
            background: '#fef2f2',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#991b1b',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
          }}
        >
          <AlertCircle size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
          <div>
            <strong>Erro ao aplicar.</strong>
            <div style={{ marginTop: '2px' }}>{proposal.error_message || 'Tenta novamente.'}</div>
          </div>
        </div>
      )}

      {localError && (
        <div
          style={{
            marginTop: '10px',
            padding: '6px 10px',
            background: '#fef2f2',
            borderRadius: '6px',
            fontSize: '12px',
            color: '#991b1b',
          }}
        >
          {localError}
        </div>
      )}
    </div>
  )
}

function StatusBadge({
  status,
  appliedAction,
}: {
  status: EmailLabelProposal['status']
  appliedAction: string | null
}) {
  const styles: Record<EmailLabelProposal['status'], { bg: string; fg: string; label: string }> = {
    pending: { bg: '#fef3c7', fg: '#92400e', label: 'A aguardar' },
    applied: { bg: '#dcfce7', fg: '#166534', label: appliedAction === 'trash' ? 'No lixo' : 'Aplicado' },
    rejected: { bg: '#f3f4f6', fg: '#4b5563', label: 'Ignorado' },
    failed: { bg: '#fecaca', fg: '#991b1b', label: 'Erro' },
  }
  const s = styles[status]
  return (
    <span
      style={{
        padding: '2px 10px',
        borderRadius: '9999px',
        fontSize: '11px',
        fontWeight: 600,
        background: s.bg,
        color: s.fg,
        whiteSpace: 'nowrap',
      }}
    >
      {s.label}
    </span>
  )
}
